import { useCallback, useMemo, useRef, useState } from 'react';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { Menu, Typography, MenuItem, Button, CircularProgress } from '@mui/material';
import { theme } from '@/theme/theme';
import { ButtonLabel, textButtonStyle, containedButtonStyle } from '@/theme';
import { GenericConfirmationDialog } from '@/components/Analysis/shared/GeneriConfirmationDialog';
import { downloadTemplate } from '@/utils';
import { useIdmsData } from '@/hooks/useIdmsData';
import { plannerEquipmentTemplate } from '@/components/Analysis/Idms/data';
import { FileExceptionModal } from '@/components/Analysis/Idms/Modals/FileExceptionModal';
import { FileFixingUploadLoadingModal } from '../../shared';

type Props = {
  plannerEquipmentData: null | HTMLElement;
  setPlannerEquipmentData: (value: null | HTMLElement) => void;
};

export const PlannerEquipmentUpload = ({ plannerEquipmentData, setPlannerEquipmentData }: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)
  const open = Boolean(plannerEquipmentData);
  const fileInputReference = useRef<HTMLInputElement | null>(null);
  const handleClose = useCallback(() => {
    if (fileInputReference.current) fileInputReference.current.value = '';
    setPlannerEquipmentData(null);
  }, [setPlannerEquipmentData]);

  const structureId = useGetStructureId();

  const {
    setFileExceptionsModal,
    setSelectedFileType,
    handleXlsxFileUpload,
    handleCsvFileUpload,
    handleIdmsFileUpload,
    setIsValidating,
    idmsUploadLoading,
    fileExceptionsModal,
    selectedFileType,
    uploadedCsvParsedData,
    idmsValidationErrors,
    isValidating,
    openConfirmationDialog,
    setOpenConfirmationDialog,
  } = useIdmsData(structureId, true);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsValidating(true)
    setSelectedFile(file)
    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');

    const fileType = plannerEquipmentTemplate.type;
    setSelectedFileType(fileType);

    if (isXlsx) {
      handleXlsxFileUpload(file, fileType);
    } else {
      handleCsvFileUpload(file, fileType);
    }

  };


  const handleCloseFileExceptionModal = () => {
    setFileExceptionsModal(false);
    setSelectedFileType(undefined);
  };

  const MENU_ITEMS: Array<{
    title: string;
    description: string;
    onClick?: () => void;
    disabled?: boolean;
  }> = useMemo(
    () => [
      {
        title: 'Upload new file',
        description: 'Values for the equipment table',
        onClick: () => {
          fileInputReference?.current?.click?.();
          handleClose();
        },
      },
      {
        title: 'Download template',
        description: 'Download template to view all the required columns.',
        onClick: () => {
          downloadTemplate(plannerEquipmentTemplate.path, plannerEquipmentTemplate.name)
        },
      },
    ],
    [handleClose]
  );

  return (
    <>
      <Menu
        anchorEl={plannerEquipmentData}
        open={open}
        onClose={handleClose}
        sx={{
          mt: 2,
        }}
      >
        {MENU_ITEMS.map((item, index) => (
          <MenuItem
            disabled={item.disabled}
            key={index}
            onClick={item.onClick}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              my: 1,
            }}
          >
            <Typography color={theme.palette.primary.main}>{item.title}</Typography>
            <Typography color={theme.palette.primary.light}>{item.description}</Typography>
          </MenuItem>
        ))}
      </Menu>
      <input
        style={{ display: 'none' }}
        type="file"
        accept=".xlsx"
        onChange={onChange}
        ref={fileInputReference}
      />
      <GenericConfirmationDialog
        open={openConfirmationDialog}
        onClose={() => {
          setOpenConfirmationDialog(false);
          if (fileInputReference.current) fileInputReference.current.value = '';
        }}
        title="Confirm upload"
        description={
          'Please note that uploading this file will modify existing data. Review the file carefully before proceeding to ensure accuracy.'
        }
      >
        <Button
          disabled={idmsUploadLoading}
          onClick={() => {
            setOpenConfirmationDialog(false);
          }}
          variant="text"
          sx={textButtonStyle}
        >
          <ButtonLabel>Cancel</ButtonLabel>
        </Button>
        <Button
          disabled={idmsUploadLoading}
          autoFocus
          onClick={() => {
            handleIdmsFileUpload(selectedFile);
          }}
          variant="contained"
          sx={containedButtonStyle}
        >
          {idmsUploadLoading ? (
            <>
              Uploading...{' '}
              <CircularProgress color="secondary" size={30} style={{ marginLeft: 1 }} />
            </>
          ) : (
            <ButtonLabel>Confirm</ButtonLabel>
          )}
        </Button>
      </GenericConfirmationDialog>
      <FileFixingUploadLoadingModal open={isValidating} />
      <FileExceptionModal
        open={fileExceptionsModal}
        idmsUploadLoading={idmsUploadLoading}
        selectedFileType={selectedFileType}
        handleIdmsFileUpload={handleIdmsFileUpload}
        handleClose={handleCloseFileExceptionModal}
        uploadedCsvParsedData={uploadedCsvParsedData}
        idmsValidationErrors={idmsValidationErrors}
        fileInputReference={fileInputReference}
        file={selectedFile}
      />
    </>
  );
};
